export const SCHEDULE_TIME_ZONE = 'Asia/Kolkata'
export const SCHEDULE_WINDOW_START_HOUR = 8
export const SCHEDULE_WINDOW_END_HOUR = 21
export const SCHEDULE_MAX_DAYS_AHEAD = 7
export const SCHEDULE_MIN_DAYS_AHEAD = 1
export const SCHEDULE_EXPRESS_MIN_DAYS_AHEAD = 0
export const SCHEDULE_EXPRESS_MAX_DAYS_AHEAD = 7
export const SCHEDULE_MIN_LEAD_MINUTES = 60
export const SCHEDULE_IST_OFFSET = '+05:30'

export const CHECKOUT_SPLIT_MODE = {
  split: 'split',
  combined: 'combined',
} as const

export type CheckoutSplitMode =
  (typeof CHECKOUT_SPLIT_MODE)[keyof typeof CHECKOUT_SPLIT_MODE]

export const CHECKOUT_SCHEDULE_MODE = {
  asap: 'asap',
  scheduled: 'scheduled',
} as const

export type CheckoutScheduleMode =
  (typeof CHECKOUT_SCHEDULE_MODE)[keyof typeof CHECKOUT_SCHEDULE_MODE]

export const SCHEDULE_KIND = {
  standard: 'standard',
  express: 'express',
} as const

export type ScheduleKind = (typeof SCHEDULE_KIND)[keyof typeof SCHEDULE_KIND]

export type ScheduleDateOption = {
  key: string
  label: string
}

export type ScheduleSlotOption = {
  iso: string
  hour: number
  label: string
}

export type CheckoutEstimateTone = 'checking' | 'express' | 'standard' | 'split'

export type CheckoutEstimate = {
  title: string
  lead: string
  tone: CheckoutEstimateTone
}

export type CheckoutSchedulePickerProps = {
  kind: ScheduleKind
  showHeading: boolean
  heading: string
  lead: string
  asapTitle: string
  asapHint: string
  pickTitle: string
  pickHint: string
  emptyLabel: string
  mode: CheckoutScheduleMode
  onModeChange: (mode: CheckoutScheduleMode) => void
  dates: ScheduleDateOption[]
  selectedDate: string
  onDateChange: (date: string) => void
  slots: ScheduleSlotOption[]
  selectedSlot: string | null
  onSlotChange: (iso: string) => void
  plain: boolean
  disabled: boolean
}

export type CheckoutDeliveryPlanProps = {
  estimate: CheckoutEstimate
  canSplit: boolean
  canSchedule: boolean
  canScheduleExpress: boolean
  expressItems: string
  standardItems: string
  splitMode: CheckoutSplitMode
  onSplitModeChange: (mode: CheckoutSplitMode) => void
  scheduleMode: CheckoutScheduleMode
  onScheduleModeChange: (mode: CheckoutScheduleMode) => void
  dates: ScheduleDateOption[]
  selectedDate: string
  onDateChange: (date: string) => void
  slots: ScheduleSlotOption[]
  selectedSlot: string | null
  onSlotChange: (iso: string) => void
  expressScheduleMode: CheckoutScheduleMode
  onExpressScheduleModeChange: (mode: CheckoutScheduleMode) => void
  expressDates: ScheduleDateOption[]
  expressSelectedDate: string
  onExpressDateChange: (date: string) => void
  expressSlots: ScheduleSlotOption[]
  expressSelectedSlot: string | null
  onExpressSlotChange: (iso: string) => void
  disabled: boolean
}

export const SCHEDULE_UI = {
  planHeading: 'Delivery',
  estimateStandard: 'Standard delivery',
  estimateStandardLead: 'Arrives from tomorrow.',
  estimateSplit: 'Your order arrives in two parts',
  estimateSplitLead:
    'Some items can arrive in about 60 minutes, the rest from tomorrow. Choose a time for each part below, or send everything together as one standard order.',
  estimateSplitLeadFixed:
    'Some items can arrive in about 60 minutes, the rest from tomorrow. Choose a time for each part below.',
  estimateChecking: 'Checking delivery for your address',
  estimateCheckingLead: 'Checking if this order can arrive within 60 minutes.',
  planLead: '60-minute items and standard items are listed separately. Choose how to place them.',
  splitTitle: 'Two orders',
  splitHint:
    '60-minute items can leave now or on a slot from today through the next 7 days. Standard items ship as a second order from tomorrow.',
  combinedTitle: 'One standard order',
  combinedHint: 'Place everything together as standard delivery. 60-minute delivery will not apply.',
  scheduleHeading: 'Standard delivery time',
  expressGroupHeading: '60-minute items',
  standardGroupHeading: 'Standard items',
  scheduleExpressAsapTitle: 'Within 60 mins',
  scheduleExpressAsapHint: '',
  scheduleExpressPickTitle: 'Pick a time',
  scheduleExpressPickHint: 'Today or later',
  scheduleLeadStandard:
    'Standard delivery starts tomorrow. Pick a slot between 8:00 AM and 9:00 PM over the next 7 days.',
  scheduleLeadCombined:
    'This will ship as one standard order from tomorrow. Pick a slot between 8:00 AM and 9:00 PM over the next 7 days.',
  scheduleAsapTitle: 'Earliest slot',
  scheduleAsapHint: 'From tomorrow',
  schedulePickTitle: 'Pick a time',
  schedulePickHint: 'Choose day & slot',
  dateLabel: 'Date',
  slotLabel: 'Time slot',
  todayLabel: 'Today',
  tomorrowLabel: 'Tomorrow',
  noSlots: 'No slots left on this date. Pick another day.',
  expressNoSlots: 'No slots left on this date. Pick another day.',
  required: 'Choose a delivery slot for the standard order.',
  expressRequired: 'Choose a delivery slot for 60-minute delivery.',
  scheduledPrefix: 'Scheduled',
} as const
